import {
  ActionData,
  ActionIconText,
  Col,
  ColumnConfig,
  DoubleLineData,
  Row,
  TableRowData,
} from "@/components";
import clsx from "clsx";
import "./TableMobile.scss";
import { Download, Info, TriangleDown, TriangleUp } from "@/icons";
import { ReactNode } from "react";
import * as React from "react";
import { CommonIconsType } from "@/types/icons.ts";
import { useState } from "react";

export type TableMobileProps = {
  data: TableRowData[];
  columns: ColumnConfig[];
  pagination?: boolean;
  resultsPerPage?: number;
  totalResults?: number;
  initialCurrentPage?: number;
  serverSidePagination?: boolean;
  totalItems?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageChange?: (page: number) => void;
  setCurrentPage?: (page: number) => void;
  sortConfig?: { key: string; direction: "asc" | "desc" } | null;
  onSortChange?: (key: string, direction: "asc" | "desc") => void;
};

export const TableMobile = ({
  data,
  columns,
  pagination = true,
  resultsPerPage = 5,
  totalResults,
  initialCurrentPage = 1,
  serverSidePagination = false,
  totalItems,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onPageChange,
  setCurrentPage: externalSetCurrentPage,
  sortConfig = null,
  onSortChange,
}: TableMobileProps) => {
  const [currentPage, setInternalCurrentPage] = useState(initialCurrentPage);
  const handleSetCurrentPage = (page: number) => {
    setInternalCurrentPage(page);
    externalSetCurrentPage?.(page);
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      const aText =
        typeof aValue === "object" && "firstLine" in aValue
          ? aValue.firstLine
          : aValue;
      const bText =
        typeof bValue === "object" && "firstLine" in bValue
          ? bValue.firstLine
          : bValue;

      if (aText < bText) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aText > bText) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const startIndex = serverSidePagination
    ? 0
    : (currentPage - 1) * resultsPerPage;
  const endIndex = serverSidePagination
    ? sortedData.length
    : Math.min(startIndex + resultsPerPage, sortedData.length);
  const currentPageData = sortedData.slice(startIndex, endIndex);

  return (
    <Col className={"w-full h-full"} gap={"x16"}>
      {currentPageData.map((data, index) => (
        <CardTable
          columns={columns}
          key={index}
          data={data}
          sortConfig={sortConfig}
          onSortChange={onSortChange}
        />
      ))}

      {pagination && (
        <TableMobilePagination
          currentPage={currentPage}
          totalResults={totalResults || sortedData.length}
          resultsPerPage={resultsPerPage}
          setCurrentPage={handleSetCurrentPage}
          onPageChange={onPageChange}
          serverSide={serverSidePagination}
          totalItems={totalItems}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          currentResultsPerPage={
            serverSidePagination ? sortedData.length : resultsPerPage
          }
          className="mt-x16"
        />
      )}
    </Col>
  );
};

export type CardTableProps = {
  columns: ColumnConfig[];
  data: TableRowData;
  sortConfig: { key: string; direction: "asc" | "desc" } | null;
  onSortChange?: (key: string, direction: "asc" | "desc") => void;
};

const ActionContent: React.FC<{ data: ActionData }> = ({ data }) => {
  if (data.type === "link" && data.link) {
    return (
      <a href={data.link} aria-label={data.ariaLabel}>
        {data.label}
      </a>
    );
  }
  return (
    <button onClick={data.action} aria-label={data.ariaLabel}>
      {data.label}
    </button>
  );
};

export const CardTable = ({
  columns,
  sortConfig,
  data,
  onSortChange,
}: CardTableProps) => {
  const renderColumnValue = (value: any): ReactNode => {
    if (!value) return "";

    if (typeof value === "object" && "firstLine" in value) {
      const doubleLineValue = value as DoubleLineData;
      return `${doubleLineValue.firstLine}${doubleLineValue.secondLine ? ` ${doubleLineValue.secondLine}` : ""}`;
    }

    if (typeof value === "object" && "label" in value && "type" in value) {
      return <ActionContent data={value} />;
    }

    return value;
  };

  const renderIcon = (icon: React.ReactNode | CommonIconsType) => {
    switch (icon) {
      case "info":
        return <Info />;
      case "download":
        return <Download />;
      default:
        return icon;
    }
  };

  const handleSortClick = (key: string) => {
    if (!onSortChange || !sortConfig) return;

    const newDirection =
      sortConfig.key === key
        ? sortConfig.direction === "asc"
          ? "desc"
          : "asc"
        : "asc";

    onSortChange(key, newDirection);
  };

  return (
    <div
      className={clsx("ui-card-table shadow br-x4 text-slate-28 fs-custom ")}
    >
      {columns.map((column, index) => {
        if (!column.key || column.key === "actionData") {
          return (
            <Row
              key={index}
              p={"x16"}
              className={"ui-table-header"}
              justifyContent={"space-between"}
            >
              <Col justifyContent={"end"} gap={"x4"} className={"w-full"}>
                <Row justifyContent={"end"} className="table-mobile-actions">
                  {renderColumnValue(data.actionData)}
                </Row>
              </Col>
            </Row>
          );
        }

        const columnValue = data[column.key];
        const config = column;
        const isSorted = sortConfig?.key === column.key;
        const sortDirection = isSorted ? sortConfig.direction : null;

        return (
          <Row
            key={index}
            p={"x16"}
            className={"ui-table-header"}
            justifyContent={"space-between"}
          >
            <Col justifyContent={"center"} gap={"x4"} className={"w-full"}>
              <ActionIconText
                justifyContent={"space-between"}
                alignItems={"center"}
                icon={renderIcon(config.icon)}
                noIcon={!renderIcon(config.icon)}
                textLabel={config.label}
                iconPosition={config.iconPosition}
                popupInfo={config.popupInfo}
                positionPopUp={"left"}
                classNameText={"fw-bold text-slate-28 fs-custom md-fs-6"}
              >
                {config.sortable && (
                  <span
                    className="sort-indicator"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSortClick(column.key!);
                    }}
                  >
                    <TriangleUp
                      filled={isSorted && sortDirection === "desc"}
                      className={clsx("sort-indicator__triangle", {
                        active: isSorted && sortDirection === "desc",
                      })}
                    />
                    <TriangleDown
                      filled={isSorted && sortDirection === "asc"}
                      className={clsx("sort-indicator__triangle", {
                        active: isSorted && sortDirection === "asc",
                      })}
                    />
                  </span>
                )}
              </ActionIconText>
              <Row>
                <span className="text-break">
                  {renderColumnValue(columnValue)}
                </span>
              </Row>
            </Col>
          </Row>
        );
      })}
    </div>
  );
};

export type TableMobilePaginationProps = {
  currentPage?: number;
  totalResults?: number;
  resultsPerPage: number;
  setCurrentPage?: (page: number) => void;
  onPageChange?: (page: number) => void;
  className?: string;
  serverSide?: boolean;
  totalItems?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  currentResultsPerPage?: number;
};

export const TableMobilePagination: React.FC<TableMobilePaginationProps> = ({
  currentPage = 1,
  totalResults = 0,
  resultsPerPage,
  setCurrentPage,
  onPageChange,
  className,
  serverSide = false,
  totalItems,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  currentResultsPerPage = resultsPerPage,
}) => {
  const totalPages = serverSide
    ? Math.ceil((totalItems || totalResults) / resultsPerPage)
    : Math.ceil(totalResults / resultsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage?.(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    if (serverSide && onNextPage) {
      onNextPage();
    }
    handlePageChange(nextPage);
  };

  const handlePreviousPage = () => {
    const prevPage = currentPage - 1;
    if (serverSide && onPreviousPage) {
      onPreviousPage();
    }
    handlePageChange(prevPage);
  };

  const isNextDisabled = serverSide ? !hasNextPage : currentPage === totalPages;
  const isPrevDisabled = serverSide ? !hasPreviousPage : currentPage === 1;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className={clsx("pagination-mobile", className)}>
      <div className="pagination-mobile__info">
        Stai visualizzando{" "}
        <span className="pagination-mobile__info__resultsPerPage">
          {serverSide
            ? currentResultsPerPage
            : currentPage === totalPages && totalResults % resultsPerPage !== 0
              ? totalResults % resultsPerPage
              : resultsPerPage}
        </span>{" "}
        di {serverSide ? totalItems || totalResults : totalResults} risultati
      </div>
      <div className="pagination-mobile__nav">
        <button
          className={clsx(
            "pagination-mobile__button",
            isPrevDisabled && "pagination-mobile__button--disabled",
          )}
          onClick={handlePreviousPage}
          disabled={isPrevDisabled}
          aria-label="Previous page"
        >
          <svg
            className="pagination-mobile__button-arrow"
            viewBox="0 0 24 24"
            aria-hidden="true"
            role="presentation"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {getPageNumbers().map((page, index) =>
          page === "..." ? (
            <span
              key={`ellipsis-${index}`}
              className="pagination-mobile__ellipsis"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              className={clsx(
                "pagination-mobile__button",
                currentPage === page && "pagination-mobile__button--active",
              )}
              onClick={() => handlePageChange(page as number)}
              disabled={page === currentPage}
            >
              {page}
            </button>
          ),
        )}

        <button
          className={clsx(
            "pagination-mobile__button",
            isNextDisabled && "pagination-mobile__button--disabled",
          )}
          onClick={handleNextPage}
          disabled={isNextDisabled}
          aria-label="Next page"
        >
          <svg
            className="pagination-mobile__button-arrow"
            viewBox="0 0 24 24"
            aria-hidden="true"
            role="presentation"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};
